/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


// import '@gjensidige/core-navigation/dist/style.css';
import '../less/nav.less';

import React, { useEffect, useRef, useState } from 'react';
import { NavMenu, NavProps, NavUserResponse } from '@internal/schema-client';
import { canUseDOM } from '@internal/utils/can-use-dom';
import superagent from 'superagent';
import { Link } from '@gjensidige/builders-components';
import { Lock } from '@gjensidige/builders-icons';
import { Nav } from '@gjensidige/core-navigation';

import { ApiToNavItems } from './apiToNavItems';
import { SearchModal } from './search';

export const Navigation = ({
  userApiUrl = '/_/service/com.gjensidige.builders.theme/api-user',
  baseUrl = '/',
  menuData,
  currentPath = '/'
}: NavProps) => {
  const ref = useRef(null);
  const {
    actions = [],
    items = [],
    language = 'en',
    search,
    languageSwitch = undefined
  } = menuData || {};
  const [notifications, setNotifications] = useState({});
  const [userData, setUserData] = useState<NavUserResponse>({
    isAuthorized: false
  });

  // Recursively set isExpanded to items, but also actions to prevent item highlight if current url is action
  addIsExpandedToItems(currentPath, [...items, ...actions]);

  // Allow positon: sticky to properly function
  useEffect(() => {
    if (ref.current)
      ref.current.parentElement.className = 'buildersPlatformNavigationWrapper';
  }, []);

  useEffect(() => {
    // TMP fix for log out clearing sessionstorage
    if (window.location.pathname === '/utlogget') {
      try {
        window.sessionStorage.removeItem('userData');
      } catch (err) {
        // No sessionStorage
      }
      return;
    }

    const sessionUser = getSessionStorageOrDefault('userData', userData);
    const updateUserData = async () => {
      try {
        const res = await superagent
          .get(userApiUrl)
          .accept('application/json')
          .set('Applikasjon', 'INTERNETT')
          .send();
        setUserData(normalizeUserResponse(res));
      } catch (err) {
        console.log('Error fetching user-data');
      }
    };
    if (sessionUser.isAuthorized) setUserData(sessionUser);
    else updateUserData();
    storeQueryParamsInSessionStorage();
  }, []);

  useEffect(() => {
    if (userData.isAuthorized) {
      if (window && window.sessionStorage) {
        sessionStorage.setItem('userData', JSON.stringify(userData));
      }
      if (menuData.notifications?.length > 0) {
        menuData.notifications.forEach((request) =>
          getNotifications(request).then((nextNotifications) => {
            setNotifications((prevNotifications) => ({
              ...prevNotifications,
              ...nextNotifications
            }));
          })
        );
      }
    }
  }, [userData]);

  const [showModal, setShowModal] = useState(false);
  const onToggle = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (event.type === 'click' || (event as React.KeyboardEvent).key === ' ') {
      event.preventDefault();
      setShowModal(!showModal);
    }
  };

  const currentRootLang =
    languageSwitch !== undefined
      ? languageSwitch
          .map(({ url }) => url)
          .sort((a, b) => b.length - a.length) // Ensure shorter urls are put last to prevent false positives
          .find((url) => currentPath.startsWith(url))
      : '';

  return (
    <>
      <Nav
        homeUrl={menuData.homeUrl}
        id={'buildersPlatformMainNavigation'}
        isLoggedIn={false}
        language={language}
        ref={ref}
        skipId="content"
        systemMessages={`${baseUrl}api-builders/system-messages`}
        className={'buildersPlatformNavigationTempMarginBottomFix'}
      >
        <ApiToNavItems
          currentPath={currentPath}
          homeUrl={menuData.homeUrl}
          items={items}
          notifications={notifications}
        />
        <Nav.Search
          href={search.action.href}
          onClick={onToggle}
          onKeyDown={onToggle}
          role="button"
        />
        <Nav.Actions>
          <Nav.Cart />
          {/* Empty span fixes React hydration bug */}
          <span />
          <ApiToNavItems currentPath={currentPath} items={actions} />

          {languageSwitch !== undefined && (
            <div className={'buildersPlatformNavigationLanguageSwitch'}>
              {languageSwitch.map((lang, index) => (
                <>
                  <Link
                    href={lang.url}
                    key={`${lang.language}${index}`}
                    aria-current={currentRootLang === lang.url}
                  >
                    {lang.language.toUpperCase()}
                  </Link>
                </>
              ))}
            </div>
          )}
        </Nav.Actions>
        {languageSwitch === undefined &&
          (userData.isAuthorized ? (
            <Nav.User
              label={userData.fullname}
              href={
                menuData.authentication?.loggedInUrl ||
                menuData.authentication?.loginUrl
              }
            />
          ) : (
            <Nav.User
              icon={<Lock />}
              href={menuData.authentication?.loginUrl}
            />
          ))}

        {canUseDOM() && (
          <SearchModal
            showModal={showModal}
            onClickModal={() => setShowModal(false)}
            {...search}
          />
        )}
      </Nav>
    </>
  );
};

Navigation.CSR = true;
Navigation.getServerSideProps = async function ({ menuApiUrl }) {
  if (!menuApiUrl) {
    console.error(
      'missing required menuApiUrl prop set by NAV_API_URL env var'
    );
  }

  const res = await superagent
    .get(menuApiUrl)
    .accept('application/json')
    .send();

  return { menuData: res.body };
};

export default Navigation;

// Utilities
function getSessionStorageOrDefault(key: string, defaultValue) {
  try {
    return JSON.parse(window.sessionStorage.getItem(key)) || defaultValue;
  } catch (err) {
    return defaultValue;
  }
}

function normalizeUserResponse(res: superagent.Response): NavUserResponse {
  const userResponse = {
    isAuthorized: false,
    fullname: '',
    customerType: ''
  };

  if (res.statusCode !== 200) return userResponse;

  // Normalize api-user/v1 used by NO
  try {
    if (res.body?.personalia && res.body?.identifikasjon) {
      const { partref } = res.body.identifikasjon;
      const { fornavn, etternavn } = res.body.personalia;
      if (fornavn && etternavn && partref) {
        return {
          isAuthorized: true,
          fullname: `${fornavn} ${etternavn}`,
          customerType: partref
        };
      }
    }
  } catch (err) {
    console.log(`failed to normalize api-user/v1 response`);
  }

  // Normalize web-api-sweden/customer for SE and api/user for DK
  try {
    const { name: fullname, customerType } = res.body;
    if (fullname) {
      return {
        isAuthorized: true,
        fullname,
        customerType
      };
    }
  } catch (err) {
    console.log(`failed to normalize web-api-sweden/customer response`);
  }
  return userResponse;
}

async function getNotifications(request: NavMenu['notifications'][0]) {
  const headers = {};
  const headersArray = [].concat(request.headers || []);
  headersArray.forEach(({ key, value }) => (headers[key] = value));

  try {
    return await fetch(request.endpoint, {
      method: 'GET',
      credentials: 'include',
      headers
    }).then((response) => response.json());
  } catch (e) {
    console.log(`Error fetching notifications from: ${request.endpoint}`);
    return {};
  }
}

function addIsExpandedToItems(currentPath: string, items: NavMenu['items']) {
  // Last item in the collection containing the path should be current, assuming shorter urls are earlier in order
  const hrefs = items.map(({ href }) => href).filter(Boolean);
  const hrefsByAscendingLength = hrefs.sort((a, b) => a.length - b.length);
  const currentLevelPath = hrefsByAscendingLength.reduce((acc, href) => {
    return currentPath.startsWith(href) ? href : acc;
  }, currentPath);

  // Add highlight to items that either contain the current item, or the current url
  return items?.some((item) => {
    return (item.isExpanded =
      addIsExpandedToItems(currentPath, item.children) ||
      currentLevelPath === item.href);
  });
}

function storeQueryParamsInSessionStorage() {
  try {
    const urlSearchParams = new URLSearchParams(location.search);
    const sessionStorageItems = sessionStorage.getItem('builders.queryParams');
    const queryParams = JSON.parse(sessionStorageItems) || {};
    urlSearchParams.forEach((value, key) => {
      queryParams[key] = value;
      //special functionality for sales norway, adding partner to session storage
      if (key === 'partner') {
        sessionStorage.setItem(
          'nettbutikk.partner',
          JSON.stringify({
            partner: value,
            source: 'PARAM'
          })
        );
      }
    });
    sessionStorage.setItem('builders.queryParams', JSON.stringify(queryParams));
  } catch (err) {
    // sessionStorage is full or URLSearchParams is not supported
  }
}
